// SosBanner — persistent alert strip shown above the main nav whenever this
// school has an active SOS. Polling-based (15s), not push-based: see
// backend/src/routes/sos.js for why (no FCM infra on either end yet).

import { useEffect, useState } from 'react';
import { getSosAlerts, acknowledgeSos, resolveSos } from '../api/client';
import SosLocationModal from './SosLocationModal';

function timeAgo(iso) {
  const seconds = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ago`;
}

export default function SosBanner() {
  const [activeAlerts, setActiveAlerts] = useState([]);
  const [viewing, setViewing] = useState(null);

  useEffect(() => {
    load();
    const interval = setInterval(load, 15000);
    return () => clearInterval(interval);
  }, []);

  async function load() {
    try {
      const alerts = await getSosAlerts('active');
      setActiveAlerts(alerts);
    } catch {
      // Not logged in yet, or a transient error — banner just stays as-is
      // until the next poll; this is a background convenience, not worth
      // surfacing its own error UI.
    }
  }

  async function handleAcknowledge(id) {
    try {
      await acknowledgeSos(id);
      setViewing(null);
      load();
    } catch {
      // swallow — the alert stays active and the next poll/manual retry will catch it
    }
  }

  async function handleResolve(id) {
    try {
      await resolveSos(id);
      setViewing(null);
      load();
    } catch {
      // swallow — same as handleAcknowledge above
    }
  }

  if (activeAlerts.length === 0) return null;

  const first = activeAlerts[0];

  return (
    <>
      <div className="bg-critical text-white px-6 py-3 flex items-center justify-between animate-pulse">
        <div className="flex items-center gap-3">
          <span className="text-2xl">🚨</span>
          <div>
            <strong>ACTIVE SOS ALERT</strong>
            <span className="ml-4">
              {first.driver_name} — {first.message} — {timeAgo(first.triggered_at)}
            </span>
            {activeAlerts.length > 1 && <span> (+{activeAlerts.length - 1} more)</span>}
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setViewing(first)} className="bg-white text-critical font-bold px-4 py-1.5 rounded-s">📍 View Location</button>
          <button onClick={() => handleAcknowledge(first.id)} className="bg-white/20 text-white border border-white px-4 py-1.5 rounded-s">✓ Acknowledge</button>
        </div>
      </div>

      {viewing && (
        <SosLocationModal
          alert={viewing}
          onClose={() => setViewing(null)}
          onAcknowledge={handleAcknowledge}
          onResolve={handleResolve}
        />
      )}
    </>
  );
}
